import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { fetchDestination } from "@/lib/destinations"
import { destinationKey } from "@/lib/destination-keys"
import { ExploreCard } from "@/components/ExploreCard"
import { CopyingTripOverlay } from "@/components/CopyingTripOverlay"
import { useCopyGuide } from "@/lib/hooks/use-copy-guide"
import { guideSlug } from "@/lib/slugify"
import { defaultMeta, noIndex, absoluteUrl, SITE_NAME } from "@/lib/seo"

export const Route = createFileRoute(`/_public/destinations/$slug`)({
  ssr: true,
  // fetchDestination resolves to a DestinationDetail, or null for a GENUINE miss
  // (unknown slug / below the thin-content threshold), but THROWS on a transient
  // DB error. We catch that here as `undefined` so head() can tell a real 404
  // (noindex is correct) from a DB blip (must stay indexable) — a raw-HTML
  // noindex on a real hub once deindexed the whole guide library. A below-
  // threshold/unknown slug renders a proper not-found in-component, never throws
  // (a thrown loader renders the error boundary as indexed HTML).
  loader: ({ params }) =>
    fetchDestination({ data: { slug: params.slug } }).catch(() => undefined),
  head: ({ params, loaderData }) => {
    // Transient loader failure (DB blip): the hub may well be real, so emit
    // neutral INDEXABLE meta with a self-canonical and bail before the noindex
    // branch. Mirrors the guide route's transient-error guard.
    if (loaderData === undefined) {
      return defaultMeta({ path: `/destinations/${params.slug}` })
    }
    // Genuinely-not-found hub (unknown / below the thin-content threshold): this
    // is a real 404, so noindex it. The sitemap and the index only ever link to
    // hubs that exist, so this only fires for a slug with no qualifying hub.
    if (!loaderData) {
      return defaultMeta({
        path: `/destinations/${params.slug}`,
        robots: noIndex,
      })
    }
    return defaultMeta({
      title: `Things to do in ${loaderData.destination}`,
      description: `Travel guides and day-by-day itineraries for ${loaderData.destination} — real trips published by ChicTravel travelers, free to read and copy into your own plan.`,
      path: `/destinations/${params.slug}`,
    })
  },
  component: DestinationPage,
})

function DestinationPage() {
  const { slug } = Route.useParams()
  const loaderData = Route.useLoaderData()
  // Client cache seeded from the loader (factory key, never an inline array) so
  // the SSR'd grid is real markup and a post-hydration refetch keeps the guide
  // list/counts fresh. Disabled when there's no hub to fetch.
  const { data } = useQuery({
    queryKey: destinationKey(slug),
    queryFn: () => fetchDestination({ data: { slug } }),
    initialData: loaderData ?? undefined,
    // Refetch when the loader resolved a hub OR failed transiently (undefined) —
    // a DB blip during SSR then self-heals after hydration. A genuine 404 (null)
    // stays disabled: no point re-asking for a hub that doesn't exist.
    enabled: loaderData !== null,
  })
  // copy/like still need an account — useCopyGuide handles the login-wall bounce
  // and the optimistic overlay. (Like buttons are omitted on the hub, so the
  // cards show static like badges — the heavier like cache lives on explore.)
  const { copyingGuide, copyGuide } = useCopyGuide(`destination_hub`)

  const detail = data ?? loaderData
  if (!detail) return <DestinationNotFound slug={slug} />

  const { destination, guides } = detail

  // ItemList of the guides inside a CollectionPage so the hub reads as a
  // structured list. SSR'd inline (full SSR route) → present in raw HTML.
  const jsonLd = {
    "@context": `https://schema.org`,
    "@type": `CollectionPage`,
    name: `Things to do in ${destination}`,
    url: absoluteUrl(`/destinations/${slug}`),
    publisher: { "@type": `Organization`, name: SITE_NAME, url: absoluteUrl(`/`) },
    mainEntity: {
      "@type": `ItemList`,
      itemListElement: guides.map((g, i) => ({
        "@type": `ListItem`,
        position: i + 1,
        name: g.name || destination,
        // Canonical on short_id, like every other guide surface — never the uuid.
        url: absoluteUrl(`/guide/${guideSlug(g)}/${g.short_id}`),
      })),
    },
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 space-y-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <CopyingTripOverlay trip={copyingGuide} />

      <header className="space-y-3 max-w-3xl">
        <p className="text-sm font-medium text-muted-foreground">
          <Link to="/destinations" className="hover:text-foreground hover:underline">
            Destinations
          </Link>
        </p>
        <h1 className="text-4xl font-extrabold tracking-tight">
          Things to do in {destination}
        </h1>
        <p className="text-muted-foreground text-lg leading-relaxed">
          {guides.length} {guides.length === 1 ? `itinerary` : `itineraries`} for
          {` `}
          {destination}, each a real, complete trip published by a ChicTravel
          traveler — day-by-day plans with every stop pinned on a live map. Read
          any one free, then copy it in a tap to make it your own editable plan.
        </p>
      </header>

      <section className="space-y-5">
        <h2 className="text-2xl font-bold tracking-tight">
          {destination} itineraries
        </h2>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))] lg:grid-cols-4">
          {guides.map((g) => (
            <ExploreCard
              key={g.id}
              item={g}
              href={`/guide/${guideSlug(g)}/${g.short_id}`}
              onCopy={() =>
                void copyGuide({
                  id: g.id,
                  name: g.name,
                  destination: g.destination,
                  image_url: g.image_url,
                })
              }
              isCopying={copyingGuide?.id === g.id}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

function DestinationNotFound({ slug }: { slug: string }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center space-y-4">
      <h1 className="text-2xl font-bold">Destination not found</h1>
      <p className="text-muted-foreground">
        We don&apos;t have enough published guides for{` `}
        <span className="font-medium">{slug}</span> yet.
      </p>
      <Link
        to="/destinations"
        className="text-primary font-medium hover:underline"
      >
        ← Browse all destinations
      </Link>
    </div>
  )
}
